import { QueueEntryApi } from "./types";

/**
 * Règles d'affichage de la liste d'attente, partagées par les vues médecin et
 * secrétaire. Aucune ne modifie l'ordre FIFO (DEC-8).
 */

/** En dessous : attente normale (gris). */
export const WAIT_WARNING_MINUTES = 15;
/** Au-delà : attente longue (rose). Entre les deux : à surveiller (ambre). */
export const WAIT_ALERT_MINUTES = 30;

export type WaitLevel = "normal" | "warning" | "alert";

export const waitLevel = (minutes: number): WaitLevel => {
  if (minutes > WAIT_ALERT_MINUTES) return "alert";
  if (minutes >= WAIT_WARNING_MINUTES) return "warning";
  return "normal";
};

/**
 * Un motif affiché en rouge signale une urgence (convention du catalogue :
 * « Urgence » est rouge). Simple signalement : l'ordre de passage ne change pas.
 */
export const URGENT_REASON_COLOR = "RED";

export const isUrgent = (entry: QueueEntryApi): boolean =>
  entry.visitReasonColor === URGENT_REASON_COLOR;

export type QueueStats = {
  waitingCount: number;
  withAppointmentCount: number;
  averageWaitMinutes: number | null;
  longestWait: QueueEntryApi | null;
  urgentCount: number;
};

/** Indicateurs calculés sur la liste déjà chargée : aucun appel serveur. */
export const computeQueueStats = (entries: QueueEntryApi[]): QueueStats => {
  const waits = entries
    .map((entry) => entry.waitingMinutes)
    .filter((minutes): minutes is number => minutes != null);
  const longestWait = entries.reduce<QueueEntryApi | null>(
    (longest, entry) =>
      (entry.waitingMinutes ?? -1) > (longest?.waitingMinutes ?? -1) ? entry : longest,
    null
  );
  return {
    waitingCount: entries.length,
    withAppointmentCount: entries.filter((entry) => !entry.walkIn).length,
    averageWaitMinutes: waits.length
      ? Math.round(waits.reduce((sum, minutes) => sum + minutes, 0) / waits.length)
      : null,
    longestWait,
    urgentCount: entries.filter(isUrgent).length,
  };
};

/** Libellé « Prénom NOM » d'une entrée. */
export const patientDisplayName = (entry: Pick<QueueEntryApi, "patientFirstName" | "patientLastName">) =>
  [entry.patientFirstName, entry.patientLastName?.toUpperCase()].filter(Boolean).join(" ");
